import React, { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, Platform, StyleSheet } from 'react-native';
import { getFirestore, collection, addDoc, doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { globalStyles } from '../styles/globalStyles';
import { venueReportStyles } from '../styles/venueReportStyles';
import { ThankYouPopup } from './ThankYouPopup';
import styled from 'styled-components';
import { addReportLibraryFileForUser } from '../utils/fileManagerUtils';
import { createExtraGradeLine, sanitizeExtraGradeLines } from '../utils/extraGradeLines';

// Custom dropdown component for web
const Dropdown = ({ value, onChange, options, placeholder, style }) => {
  if (Platform.OS === 'web') {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          ...styles.dropdown,
          ...style,
        }}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }
  
  // For non-web platforms, use a simple dropdown menu
  return (
    <View style={[styles.dropdown, style]}>
      <TouchableOpacity
        onPress={() => {
          // Show options in a simple modal or alert
          // This is a simplified version - in a real app, you'd implement a proper dropdown
          const optionValues = options.map(o => o.label).join(', ');
          alert(`Please select one of: ${optionValues}`);
        }}
      >
        <Text style={styles.dropdownText}>
          {value ? options.find(o => o.value === value)?.label : placeholder || 'Select an option'}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

// Grade dropdown options
const gradeOptions = [
  { label: 'A', value: 'A' },
  { label: 'B', value: 'B' },
  { label: 'C', value: 'C' },
  { label: 'D', value: 'D' },
  { label: 'F', value: 'F' },
];

// User type options
const userTypeOptions = [
  { label: 'Artist', value: 'artist' },
  { label: 'Employee', value: 'employee' },
  { label: 'Touring Crew', value: 'touring' },
  { label: 'Other', value: 'other' },
];

const SaveButton = styled.button`
  background-color: #000000;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-family: 'VastagoGrotesk';
  font-size: 16px;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #333333;
  }

  &:disabled {
    background-color: #666769;
    cursor: not-allowed;
  }
`;

const REQUIRED_MARK_COLOR = '#dc2626';

/** Asterisk + (required) in red; field name uses normal label styling. */
function RequiredFieldLabel({ name }) {
  return (
    <Text style={venueReportStyles.label}>
      <Text style={{ color: REQUIRED_MARK_COLOR }}>*</Text>
      {name}{' '}
      <Text style={{ color: REQUIRED_MARK_COLOR }}>(required)</Text>
    </Text>
  );
}

export function VenueReportForm({
  venue,
  onSubmit,
  onCancel,
  isTechSpecsOnly,
  initialUserType = '',
  lockUserType = false,
  embedded = false,
}) {
  const [userType, setUserType] = useState(initialUserType || '');
  const [formData, setFormData] = useState({
    // Common fields
    notes: '',
    anythingElse: '',
    extraLines: [],
    
    // Artist fields
    dateOfPerformance: '',
    bandName: '',
    ticketPrice: '',
    totalTicketsSold: '',
    payoutStructure: '',
    bandTakeHomeAmount: '',
    venueMerchCut: '',
    musicalEquipment: '',
    musicalEquipmentGrade: '',
    stageSafety: '',
    stageSafetyGrade: '',
    hospitality: '',
    backstage: '',
    venuePromotion: '',
    audioStaff: '',
    venueStaff: '',
    lightingStaff: '',
    pay: '',
    promoter: '',
    
    // Employee fields
    datesWorked: '',
    jobTitle: '',
    // productionManager: '',
    payType: '',
    payAmount: '',
    overtimeType: '',
    overtimeHours: '',
    overtimeOther: '',
    averageShiftLength: '',
    paidShadowDay: '',
    paidShadowDayAmount: '',
    consistentTraining: '',
    consistentTrainingDescription: '',
    isUnionHouse: false,
    unionName: '',
    audioEquipment: '',
    lightingEquipment: '',
    workplaceCleaniness: '',
    overallSafety: '',
    payComparison: '',
    
    // Touring Crew fields
    dayRate: '',
    venueManagement: '',
    cityWalkability: '',
    favoriteShop: '',
    
    // Technical Details (for tech specs only)
    ...(isTechSpecsOnly ? {
      stage: '',
      power: '',
      lightingControl: '',
      lightingFixtures: '',
      video: '',
      foh: '',
      pa: '',
      monitors: '',
      backline: '',
      parking: '',
      shorePower: '',
      merch: '',
      adaAccessibilityArtist: '',
    } : {}),
    
    // Media
    photos: [],
    videos: [],
    documents: [],
    
    // New Artist fields
    musicalEquipmentGrade: '',
    stageSafetyGrade: '',
    promoter: '',
    
    // New Employee fields
    payType: 'hourly', // or 'shift'
    payAmount: '',
    overtimeType: 'weekly', // or 'shift' or 'other'
    overtimeHours: '',
    overtimeOther: '',
    averageShiftLength: '',
    paidShadowDay: '',
    paidShadowDayAmount: '',
    consistentTraining: '',
    consistentTrainingDescription: '',
    isUnionHouse: false,
    unionName: '',
    
    // New Other fields
    role: '',
  });

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMediaUpload = async (type) => {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = type === 'photos' ? 'image/*' : type === 'videos' ? 'video/*' : '.pdf,.doc,.docx';
      
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const storage = getStorage();
        const auth = getAuth();
        const userId = auth.currentUser?.uid;

        const storageRef = ref(storage, `venue-reports/${venue.id}/${userId}/${Date.now()}_${file.name}`);
        
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);

        // Add file to User Uploads folder in parallel (don't block the main upload)
        // addFileToUserUploads(userId, fileName, downloadURL, 'venue_report', {
        //   venueId: venue.id,
        //   venueName: venue.name,
        //   mediaType: type,
        //   uploadDate: new Date().toISOString()
        // }).catch(error => {
        //   console.warn('Failed to add file to User Uploads folder:', error);
        //   // Don't throw - we don't want to break the report submission
        // });

        setFormData(prev => ({
          ...prev,
          [type]: [...prev[type], downloadURL]
        }));
        setUploading(false);
      };

      input.click();
    } catch (error) {
      console.error('Error uploading media:', error);
      setError('Failed to upload media. Please try again.');
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setError('');
      const auth = getAuth();
      const db = getFirestore();
      
      if (!auth.currentUser) {
        setError('You must be logged in to submit a report');
        return;
      }

      // Validate required fields based on user type
      if (!isTechSpecsOnly) {
        if (userType === 'artist' && !formData.dateOfPerformance.trim()) {
          setError('Date of Performance is required');
          return;
        }
        
        if (userType === 'employee') {
          if (!formData.datesWorked.trim()) {
            setError('Dates Worked is required');
            return;
          }
          if (!formData.jobTitle.trim()) {
            setError('Job Title is required');
            return;
          }
        }
        
        if (userType === 'touring' && !formData.jobTitle.trim()) {
          setError('Job Title is required');
          return;
        }

        if (userType === 'other' && !formData.role.trim()) {
          setError('Your Role is required');
          return;
        }
      }

      // Log venue object to debug
      console.log('Venue object:', venue);

      // Get venue ID - check all possible locations
      const venueId = venue.id || venue.venueId || venue.venue_id;
      
      if (!venueId) {
        setError('Invalid venue ID. Please try again.');
        return;
      }

      const reportData = {
        ...formData,
        extraLines: sanitizeExtraGradeLines(formData.extraLines),
        venueId: venueId,
        venueName: venue.name,
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        createdAt: new Date(),
        status: 'pending',
        isTechSpecsOnly: isTechSpecsOnly || false,
        userType: isTechSpecsOnly ? 'technical' : userType,
        ...(isTechSpecsOnly ? { submissionSource: 'vrc' } : {}),
      };

      // Log report data before submission
      console.log('Submitting report data:', reportData);

      // Add to venue_reports collection
      const reportRef = await addDoc(collection(db, 'venue_reports'), reportData);
      
      // Add to venue's reports subcollection
      await addDoc(collection(doc(db, 'venues', venueId), 'venue_reports'), {
        reportId: reportRef.id,
        ...reportData
      });

      const reportForLibrary = { id: reportRef.id, ...reportData };
      await addReportLibraryFileForUser(auth.currentUser.uid, reportForLibrary);

      setShowThankYou(true);
    } catch (error) {
      console.error('Error submitting report:', error);
      setError('Failed to submit report. Please try again.');
    }
  };

  const handleThankYouClose = () => {
    setShowThankYou(false);
    onSubmit();
  };

  const renderGradeDropdown = (label, field) => (
    <View style={venueReportStyles.formGroup}>
      <Text style={venueReportStyles.label}>
        {label}
      </Text>
      <Dropdown
        value={formData[field]}
        onChange={(value) => handleInputChange(field, value)}
        options={gradeOptions}
        placeholder="Select grade (A-F)"
        style={styles.gradeDropdown}
      />
    </View>
  );

  const updateExtraLine = (id, patch) => {
    setFormData((prev) => ({
      ...prev,
      extraLines: (prev.extraLines || []).map((line) => (
        line.id === id ? { ...line, ...patch } : line
      )),
    }));
  };

  const renderExtraLines = () => (
    <View style={venueReportStyles.section}>
      <Text style={venueReportStyles.sectionTitle}>Additional grades</Text>
      <Text style={venueReportStyles.sublabel}>
        Add a line item for anything this questionnaire doesn’t already cover. Works for every role.
      </Text>
      {(formData.extraLines || []).map((line, index) => (
        <View key={line.id} style={venueReportStyles.formGroup}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <Text style={venueReportStyles.label}>Line {index + 1}</Text>
            <TouchableOpacity
              onPress={() => handleInputChange(
                'extraLines',
                (formData.extraLines || []).filter((item) => item.id !== line.id)
              )}
            >
              <Text style={{ color: REQUIRED_MARK_COLOR, fontWeight: '600' }}>Remove</Text>
            </TouchableOpacity>
          </View>
          <TextInput
            style={venueReportStyles.input}
            value={line.label}
            onChangeText={(value) => updateExtraLine(line.id, { label: value })}
            placeholder="What are you grading?"
            placeholderTextColor="rgba(255, 255, 255, 0.5)"
          />
          <Dropdown
            value={line.grade}
            onChange={(value) => updateExtraLine(line.id, { grade: value })}
            options={gradeOptions}
            placeholder="Select grade (A-F)"
            style={styles.gradeDropdown}
          />
        </View>
      ))}
      <TouchableOpacity
        onPress={() => handleInputChange('extraLines', [...(formData.extraLines || []), createExtraGradeLine()])}
        style={{
          borderWidth: 1,
          borderStyle: 'dashed',
          borderColor: 'rgba(0,0,0,0.35)',
          borderRadius: 10,
          paddingVertical: 12,
          alignItems: 'center',
          marginTop: 4,
        }}
      >
        <Text style={{ color: '#000', fontWeight: '700' }}>+ Add line</Text>
      </TouchableOpacity>
    </View>
  );

  const renderArtistForm = () => (
    <View style={venueReportStyles.section}>
      <Text style={venueReportStyles.sectionTitle}>Artist Questions</Text>
      
      <View style={venueReportStyles.formGroup}>
        <RequiredFieldLabel name="Date or Year of Performance" />
        <TextInput
          style={venueReportStyles.input}
          value={formData.dateOfPerformance}
          onChangeText={(value) => handleInputChange('dateOfPerformance', value)}
          placeholder="Enter date or year"
          placeholderTextColor="rgba(255, 255, 255, 0.5)"
        />
      </View>

      <View style={venueReportStyles.formGroup}>
        <Text style={venueReportStyles.label}>Show Promoter Name</Text>
        <TextInput
          style={venueReportStyles.input}
          value={formData.promoter}
          onChangeText={(value) => handleInputChange('promoter', value)}
          placeholder="Enter show promoter name"
          placeholderTextColor="rgba(255, 255, 255, 0.5)"
        />
      </View>

      <View style={venueReportStyles.formGroup}>
        <Text style={venueReportStyles.label}>Ticket Price</Text>
        <TextInput
          style={venueReportStyles.input}
          value={formData.ticketPrice}
          onChangeText={(value) => handleInputChange('ticketPrice', value)}
          placeholder="Enter ticket price"
          placeholderTextColor="rgba(255, 255, 255, 0.5)"
          keyboardType="numeric"
        />
      </View>

      <View style={venueReportStyles.formGroup}>
        <Text style={venueReportStyles.label}>Total Tickets Sold</Text>
        <TextInput
          style={venueReportStyles.input}
          value={formData.totalTicketsSold}
          onChangeText={(value) => handleInputChange('totalTicketsSold', value)}
          placeholder="Enter total tickets sold"
          placeholderTextColor="rgba(255, 255, 255, 0.5)"
          keyboardType="numeric"
        />
      </View>

      <View style={venueReportStyles.formGroup}>
        <Text style={venueReportStyles.label}>Payout Structure</Text>
        <TextInput
          style={venueReportStyles.input}
          value={formData.payoutStructure}
          onChangeText={(value) => handleInputChange('payoutStructure', value)}
          placeholder="Describe payout structure"
          placeholderTextColor="rgba(255, 255, 255, 0.5)"
        />
      </View>

      <View style={venueReportStyles.formGroup}>
        <Text style={venueReportStyles.label}>Your band's take-home amount (tickets only)</Text>
        <TextInput
          style={venueReportStyles.input}
          value={formData.bandTakeHomeAmount}
          onChangeText={(value) => handleInputChange('bandTakeHomeAmount', value)}
          placeholder="Enter take-home amount"
          placeholderTextColor="rgba(255, 255, 255, 0.5)"
          keyboardType="numeric"
        />
      </View>

      <View style={venueReportStyles.formGroup}>
        <Text style={venueReportStyles.label}>Venue Merch Cut</Text>
        <TextInput
          style={venueReportStyles.input}
          value={formData.venueMerchCut}
          onChangeText={(value) => handleInputChange('venueMerchCut', value)}
          placeholder="Enter venue merch cut"
          placeholderTextColor="rgba(255, 255, 255, 0.5)"
        />
      </View>
      
      <View style={venueReportStyles.formGroup}>
        <Text style={venueReportStyles.label}>Musical Equipment Provided by Venue</Text>
        <TextInput
          style={venueReportStyles.input}
          value={formData.musicalEquipment}
          onChangeText={(value) => handleInputChange('musicalEquipment', value)}
          placeholder="Describe equipment provided"
          placeholderTextColor="rgba(255, 255, 255, 0.5)"
        />
        {renderGradeDropdown('Equipment Grade', 'musicalEquipmentGrade')}
      </View>
      
      <View style={venueReportStyles.formGroup}>
        <Text style={venueReportStyles.label}>Stage Safety (dirty power? low hanging speakers? trip hazards?)</Text>
        <TextInput
          style={venueReportStyles.input}
          value={formData.stageSafety}
          onChangeText={(value) => handleInputChange('stageSafety', value)}
          placeholder="Describe stage safety"
          placeholderTextColor="rgba(255, 255, 255, 0.5)"
        />
        {renderGradeDropdown('Safety Grade', 'stageSafetyGrade')}
      </View>
      
      {/* <View style={venueReportStyles.formGroup}>
        <Text style={venueReportStyles.label}>Promoter</Text>
        <TextInput
          style={venueReportStyles.input}
          value={formData.promoter}
          onChangeText={(value) => handleInputChange('promoter', value)}
          placeholder="Enter promoter name"
          placeholderTextColor="rgba(255, 255, 255, 0.5)"
        />
      </View> */}
      
      {renderGradeDropdown('Hospitality', 'hospitality')}
      {renderGradeDropdown('Backstage', 'backstage')}
      {renderGradeDropdown('Venue\'s Promotion of Show', 'venuePromotion')}
      {renderGradeDropdown('Audio Staff', 'audioStaff')}
      {renderGradeDropdown('Lighting Staff', 'lightingStaff')}
      {renderGradeDropdown('Pay (were you paid on time?)', 'pay')}
    </View>
  );
  
  const renderEmployeeForm = () => (
    <View style={venueReportStyles.section}>
      <Text style={venueReportStyles.sectionTitle}>Employee Questions</Text>
      
      <View style={venueReportStyles.formGroup}>
        <RequiredFieldLabel name="Dates Worked" />
        <TextInput
          style={venueReportStyles.input}
          value={formData.datesWorked}
          onChangeText={(value) => handleInputChange('datesWorked', value)}
          placeholder="Enter dates worked"
          placeholderTextColor="rgba(255, 255, 255, 0.5)"
        />
      </View>
      
      <View style={venueReportStyles.formGroup}>
        <RequiredFieldLabel name="Your Job Title" />
        <TextInput
          style={venueReportStyles.input}
          value={formData.jobTitle}
          onChangeText={(value) => handleInputChange('jobTitle', value)}
          placeholder="Enter job title"
          placeholderTextColor="rgba(255, 255, 255, 0.5)"
        />
      </View>
      
      {/* {renderGradeDropdown('Production Manager', 'productionManager')} */}
      
      <View style={venueReportStyles.formGroup}>
        <Text style={venueReportStyles.label}>Pay Rate</Text>
        <Dropdown
          value={formData.payType}
          onChange={(value) => handleInputChange('payType', value)}
          options={[
            { label: 'Hourly', value: 'hourly' },
            { label: 'Shift Amount', value: 'shift' }
          ]}
          placeholder="Select pay type"
          style={venueReportStyles.dropdown}
        />
        {formData.payType && (
          <TextInput
            style={[venueReportStyles.input, { marginTop: 10 }]}
            value={formData.payAmount}
            onChangeText={(value) => handleInputChange('payAmount', value)}
            placeholder={formData.payType === 'hourly' ? "Enter hourly rate" : "Enter shift amount"}
            placeholderTextColor="rgba(255, 255, 255, 0.5)"
            keyboardType="numeric"
          />
        )}
      </View>
      
      <View style={venueReportStyles.formGroup}>
        <Text style={venueReportStyles.label}>Overtime</Text>
        <Dropdown
          value={formData.overtimeType}
          onChange={(value) => handleInputChange('overtimeType', value)}
          options={[
            { label: 'Paid after 40 hrs worked in a week', value: 'weekly' },
            { label: 'Paid per shift', value: 'shift' },
            { label: 'Other', value: 'other' }
          ]}
          placeholder="Select overtime type"
          style={venueReportStyles.dropdown}
        />
        {formData.overtimeType === 'shift' && (
          <TextInput
            style={[venueReportStyles.input, { marginTop: 10 }]}
            value={formData.overtimeHours}
            onChangeText={(value) => handleInputChange('overtimeHours', value)}
            placeholder="Enter hours before overtime"
            placeholderTextColor="rgba(255, 255, 255, 0.5)"
            keyboardType="numeric"
          />
        )}
        {formData.overtimeType === 'other' && (
          <TextInput
            style={[venueReportStyles.input, { marginTop: 10 }]}
            value={formData.overtimeOther}
            onChangeText={(value) => handleInputChange('overtimeOther', value)}
            placeholder="Specify overtime policy"
            placeholderTextColor="rgba(255, 255, 255, 0.5)"
          />
        )}
      </View>
      <View style={venueReportStyles.formGroup}>  
        <Text style={venueReportStyles.label}>Average Shift Length</Text>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <TextInput
              style={[venueReportStyles.input, { width: '50%' }]}
              value={formData.averageShiftLength}
              onChangeText={(value) => handleInputChange('averageShiftLength', value)}
              placeholder="Enter number of hours"
              placeholderTextColor="rgba(255, 255, 255, 0.5)"
              keyboardType="numeric"
              />
            <Text style={{ marginLeft: 10 }}>hrs</Text>
        </View>
      </View>

      <View style={venueReportStyles.formGroup}>
        <Text style={venueReportStyles.label}>Paid Shadow Day / Walk Through?</Text>
        <Dropdown
          value={formData.paidShadowDay}
          onChange={(value) => handleInputChange('paidShadowDay', value)}
          options={[
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' }
          ]}
          placeholder="Select if paid shadow day / walk through"
          style={venueReportStyles.dropdown}
        />
        {formData.paidShadowDay === 'yes' && (
          <TextInput
            style={[venueReportStyles.input, { marginTop: 10 }]}
            value={formData.paidShadowDayAmount}
            onChangeText={(value) => handleInputChange('paidShadowDayAmount', value)}
            placeholder="How much?"
            placeholderTextColor="rgba(255, 255, 255, 0.5)"
            keyboardType="numeric"
          />
        )}
      </View>

      <View style={venueReportStyles.formGroup}>
        <Text style={venueReportStyles.label}>Does this venue provide consistent training to their employees?</Text>
        <Dropdown
          value={formData.consistentTraining}
          onChange={(value) => handleInputChange('consistentTraining', value)}
          options={[
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' }
          ]}
          placeholder="Select if consistent training is provided"
          style={venueReportStyles.dropdown}
        />
        {formData.consistentTraining === 'yes' && (
          <TextInput
            style={[venueReportStyles.input, { marginTop: 10 }]}
            value={formData.consistentTrainingDescription}
            onChangeText={(value) => handleInputChange('consistentTrainingDescription', value)}
            placeholder="Tell us more..."
            placeholderTextColor="rgba(255, 255, 255, 0.5)"
            multiline
            numberOfLines={4}
          />
        )}
      </View>
      {/* <View style={venueReportStyles.formGroup}>
        <Text style={venueReportStyles.label}>Is this venue a union house?</Text>
        <Dropdown
          value={formData.isUnionHouse ? 'yes' : 'no'}
          onChange={(value) => handleInputChange('isUnionHouse', value === 'yes')}
          options={[
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' }
          ]}
          placeholder="Select union status"
          style={venueReportStyles.dropdown}
        />
        {formData.isUnionHouse && (
          <TextInput
            style={[venueReportStyles.input, { marginTop: 10 }]}
            value={formData.unionName}
            onChangeText={(value) => handleInputChange('unionName', value)}
            placeholder="Please provide union name"
            placeholderTextColor="rgba(255, 255, 255, 0.5)"
          />
        )}
      </View> */}
      
      {renderGradeDropdown('Audio Equipment', 'audioEquipment')}
      {renderGradeDropdown('Lighting Equipment', 'lightingEquipment')}
      {renderGradeDropdown('Workplace Cleanliness', 'workplaceCleaniness')}
      {renderGradeDropdown('Overall Safety', 'overallSafety')}
      {renderGradeDropdown('How does this venue\'s wage and payment structure compare to other venues you\'ve worked?', 'payComparison')}
    </View>
  );
  
  const renderTouringCrewForm = () => (
    <View style={venueReportStyles.section}>
      <Text style={venueReportStyles.sectionTitle}>Touring Crew Questions</Text>
      
      <View style={venueReportStyles.formGroup}>
        <RequiredFieldLabel name="Your Job Title" />
        <TextInput
          style={venueReportStyles.input}
          value={formData.jobTitle}
          onChangeText={(value) => handleInputChange('jobTitle', value)}
          placeholder="Enter job title"
          placeholderTextColor="rgba(255, 255, 255, 0.5)"
        />
      </View>
    
      
      {renderGradeDropdown('Venue Management Communication', 'venueManagement')}
      {renderGradeDropdown('Venue Staff', 'venueStaff')}
      {renderGradeDropdown('Audio Staff', 'audioStaff')}
      {renderGradeDropdown('Lighting Staff', 'lightingStaff')}
      {renderGradeDropdown('Audio Equipment', 'audioEquipment')}
      {renderGradeDropdown('Lighting Equipment', 'lightingEquipment')}
      {renderGradeDropdown('Overall Safety', 'overallSafety')}
      {renderGradeDropdown('City Walkability', 'cityWalkability')}
      
      <View style={venueReportStyles.formGroup}>
        <Text style={venueReportStyles.label}>Share More!</Text>
        <TextInput
          style={venueReportStyles.input}
          value={formData.favoriteShop}
          onChangeText={(value) => handleInputChange('favoriteShop', value)}
          placeholder="Favorite nearby coffee shop, hotel"
          placeholderTextColor="rgba(255, 255, 255, 0.5)"
        />
      </View>

      <View style={venueReportStyles.formGroup}>
        <Text style={venueReportStyles.label}>Your Day Rate</Text>
        <TextInput
          style={venueReportStyles.input}
          value={formData.dayRate}
          onChangeText={(value) => handleInputChange('dayRate', value)}
          placeholder="Enter your day rate"
          placeholderTextColor="rgba(255, 255, 255, 0.5)"
          keyboardType="numeric"
        />
      </View>
    </View>
  );
  
  const renderOtherForm = () => (
    <View style={venueReportStyles.section}>
      <Text style={venueReportStyles.sectionTitle}>General Questions</Text>
      
      <View style={venueReportStyles.formGroup}>
        <RequiredFieldLabel name="Your Role" />
        <TextInput
          style={venueReportStyles.input}
          value={formData.role}
          onChangeText={(value) => handleInputChange('role', value)}
          placeholder="Enter your role"
          placeholderTextColor="rgba(255, 255, 255, 0.5)"
        />
      </View>

      <View style={venueReportStyles.formGroup}>
        <Text style={venueReportStyles.label}>Overall Experience</Text>
        <TextInput
          style={venueReportStyles.textArea}
          multiline
          numberOfLines={4}
          value={formData.overallExperience}
          onChangeText={(value) => handleInputChange('overallExperience', value)}
          placeholder="Describe your overall experience at this venue..."
          placeholderTextColor="rgba(255, 255, 255, 0.5)"
        />
      </View>

      {renderGradeDropdown('Safety', 'safetyRating')}
      {renderGradeDropdown('Equipment', 'equipmentRating')}
      {renderGradeDropdown('Staff', 'staffRating')}
      {renderGradeDropdown('Working Conditions', 'workingConditionsRating')}
      <View style={venueReportStyles.formGroup}>
        <Text style={venueReportStyles.label}>Anything else we should know?</Text>
        <TextInput
          style={[venueReportStyles.input, venueReportStyles.notesInput]}
          value={formData.anythingElse}
          onChangeText={(value) => handleInputChange('anythingElse', value)}
          placeholder="Anything else we should know?"
          placeholderTextColor="rgba(255, 255, 255, 0.5)"
          multiline
          numberOfLines={3}
        />
      </View>
    </View>
  );

  const renderTechSpecsForm = () => {
    // Using exact same order as view specs sheet
    const specsFields = [
      { field: 'capacity', label: 'Capacity' },
      { field: 'adaAccessibilityArtist', label: 'ADA Accessibility for Artist' },
      { field: 'parking', label: 'Parking' },
      { field: 'shorePower', label: 'Shore Power' },
      { field: 'backstage', label: 'Backstage' },
      { field: 'merch', label: 'Merch' },
      { field: 'stage', label: 'Stage' },
      { field: 'backline', label: 'Backline' },
      { field: 'video', label: 'Video' },
      { field: 'monitors', label: 'Monitors' },
      { field: 'pa', label: 'PA System' },
      { field: 'foh', label: 'FOH' },
      { field: 'lightingControl', label: 'Lighting Control' },
      { field: 'lightingFixtures', label: 'Lighting Fixtures' },
      { field: 'notes', label: 'Notes' }
    ];

    return (
      <>
        <View style={venueReportStyles.viewSpecsContainer}>
          {specsFields.map((spec, index, array) => (
            <View key={spec.field} style={venueReportStyles.specRow}>
              <View style={venueReportStyles.specRowContent}>
                <Text style={venueReportStyles.specLabel}>{spec.label}:</Text>
                <TextInput
                  style={[
                    venueReportStyles.searchBarInput,
                    spec.field === 'notes' && venueReportStyles.notesInput
                  ]}
                  value={formData[spec.field]}
                  onChangeText={(value) => handleInputChange(spec.field, value)}
                  placeholder={`Enter ${spec.label.toLowerCase()}...`}
                  placeholderTextColor="rgba(0, 0, 0, 0.6)"
                  multiline={spec.field === 'notes'}
                  numberOfLines={spec.field === 'notes' ? 4 : 1}
                />
              </View>
              {index < array.length - 1 && (
                <View style={venueReportStyles.specDivider} />
              )}
            </View>
          ))}
        </View>
      </>
    );
  };

  return (
    <>
      <ScrollView style={[
        venueReportStyles.container,
        embedded && {
          marginTop: 0,
          padding: 16,
          maxWidth: '100%',
          backgroundColor: '#e9e9e9',
        },
      ]}>
        <View style={[venueReportStyles.formContent, { paddingBottom: Platform.OS === 'web' ? 100 : 120 }]}>
          <Text style={[venueReportStyles.title, embedded && { fontSize: 22 }]}>
            {isTechSpecsOnly ? `Share Venue Specs for ${venue.name}` : `Provide a Grade for ${venue.name}`}
          </Text>
          {!isTechSpecsOnly && (
            <Text style={venueReportStyles.subtitle}>
              We will never share your identity or identifying information collected in our reports. The answers you provide contribute to charts displayed on our website(s).
            </Text>
          )}
          {error ? <Text style={venueReportStyles.errorText}>{error}</Text> : null}

          {!isTechSpecsOnly && !lockUserType && (
            <View style={venueReportStyles.userTypeContainer}>
              <Text style={venueReportStyles.label}>I am a:</Text>
              <Dropdown
                value={userType}
                onChange={setUserType}
                options={userTypeOptions}
                placeholder="Select your role"
                style={styles.userTypeDropdown}
              />
            </View>
          )}

          {isTechSpecsOnly ? (
            renderTechSpecsForm()
          ) : (
            <>
              {userType === 'artist' && renderArtistForm()}
              {userType === 'employee' && renderEmployeeForm()}
              {userType === 'touring' && renderTouringCrewForm()}
              {userType === 'other' && renderOtherForm()}
              {userType ? renderExtraLines() : null}
            </>
          )}

          {!isTechSpecsOnly && userType === 'artist' && (
            <View style={venueReportStyles.formGroup}>
              <Text style={venueReportStyles.label}>Your Band's Name (optional)</Text>
              <TextInput
                style={venueReportStyles.input}
                value={formData.bandName}
                onChangeText={(value) => handleInputChange('bandName', value)}
                placeholder="Enter band name"
                placeholderTextColor="rgba(255, 255, 255, 0.5)"
              />
            </View>
          )}

          <View style={venueReportStyles.mediaSection}>
            <Text style={venueReportStyles.sectionTitle}>Media</Text>
            <Text style={venueReportStyles.mediaText}>Add photos, videos, or documents to support your report</Text>
            
            <View style={venueReportStyles.mediaButtons}>
              <TouchableOpacity
                style={venueReportStyles.mediaButton}
                onPress={() => handleMediaUpload('photos')}
                disabled={uploading}
              >
                <Text style={venueReportStyles.mediaButtonText}>
                  {uploading ? 'Uploading...' : 'Add Photos'}
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={venueReportStyles.mediaButton}
                onPress={() => handleMediaUpload('videos')}
                disabled={uploading}
              >
                <Text style={venueReportStyles.mediaButtonText}>
                  {uploading ? 'Uploading...' : 'Add Videos'}
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={venueReportStyles.mediaButton}
                onPress={() => handleMediaUpload('documents')}
                disabled={uploading}
              >
                <Text style={venueReportStyles.mediaButtonText}>
                  {uploading ? 'Uploading...' : 'Add Documents'}
                </Text>
              </TouchableOpacity>
            </View>

            {/* Display uploaded media previews here */}
            <View style={venueReportStyles.mediaPreviewContainer}>
            {formData.photos.length > 0 && (
                <Text style={venueReportStyles.mediaCount}>
                  {formData.photos.length} photo(s) uploaded
                </Text>
              )}
              
              {formData.videos.length > 0 && (
                <Text style={venueReportStyles.mediaCount}>
                  {formData.videos.length} video(s) uploaded
                </Text>
              )}
              
              {formData.documents.length > 0 && (
                <Text style={venueReportStyles.mediaCount}>
                  {formData.documents.length} document(s) uploaded
                </Text>
              )}
              </View>
          </View>

          <View style={[venueReportStyles.buttonContainer, { marginBottom: Platform.OS === 'web' ? 40 : 80 }]}>
            <TouchableOpacity
              style={venueReportStyles.submitButton}
              onPress={handleSubmit}
            >
              <Text style={venueReportStyles.submitButtonText}>Send</Text>
            </TouchableOpacity>
            
            {/* <TouchableOpacity
              style={venueReportStyles.cancelButton}
              onPress={onCancel}
            >
              <Text style={venueReportStyles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity> */}
          </View>
        </View>
      </ScrollView>

      {showThankYou && (
        <ThankYouPopup onClose={handleThankYouClose} />
      )}
    </>
  );
} 

const styles = StyleSheet.create({
  dropdown: {
    backgroundColor: '#434252',
    width: '90%',
    color: 'white',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 5,
    padding: 10,
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none',
      cursor: 'pointer',
      ':hover': {
        borderColor: 'rgba(255, 255, 255, 0.3)',
      },
      '::placeholder': {
        color: 'rgba(255, 255, 255, 0.5)',
      },
      '& option': {
        backgroundColor: '#434252',
        color: 'white',
        padding: 10,
      }
    }),
  },
  dropdownText: {
    color: 'white',
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
  },
  gradeDropdown: {
    width: '80%',
    marginTop: 10,
    backgroundColor: '#434252',
    color: 'white',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 5,
    padding: 10,
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none',
      cursor: 'pointer',
      ':hover': {
        borderColor: 'rgba(255, 255, 255, 0.3)',
      }
    }),
  },
  userTypeDropdown: {
    width: '90%',
    marginTop: 5,
    backgroundColor: '#434252',
    color: 'white',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 5,
    padding: 10,
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none',
      cursor: 'pointer',
      ':hover': {
        borderColor: 'rgba(255, 255, 255, 0.3)',
      }
    }),
  },
}); 